import React from "react";
import FabricCartItems from "./items.jsx";

/*
 * FabricCartCheckoutComponent — the /Sample-Cart/Checkout form
 * (Lee-project port). Props: data (items), email, fabricsRootUrl, max,
 * isStaff, recipientRole.
 * Server-side rendered: ChakraCore rules — no ?. / ?? syntax.
 */

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];
const CA_PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const COUNTRIES = ["USA","CAN","AUS","BHS","BRB","BEL","BMU","BRA","CHN","COL","CRI","DNK","DOM","FRA","DEU","GRC","HKG","IND","IRL","ISR","ITA","JPN","MEX","NLD","NZL","NOR","PAN","PHL","PRT","SAU","SGP","ZAF","KOR","ESP","SWE","CHE","TWN","THA","TUR","ARE","GBR","VNM"];

const SHIP_VIA = [
  { value: "USPS", label: "US Mail - No Charge" },
  { value: "FedExD", label: "FedEx Domestic" },
  { value: "FedExI", label: "FedEx International" },
  { value: "UPSD", label: "UPS Domestic" },
  { value: "UPSI", label: "UPS International" }
];

export default class FabricCartCheckoutComponent extends React.Component {
  constructor(props) {
    super(props);
    // Signed-in users arrive with profile/address-book prefill from the
    // controller (empty object for anonymous visitors).
    var pf = props.prefill || {};
    this.state = {
      form: {
        firstName: pf.firstName || "", lastName: pf.lastName || "", company: pf.company || "",
        address: pf.address || "", address2: pf.address2 || "", city: pf.city || "",
        region: pf.region || "",
        postalCode: pf.postalCode || "", country: pf.country || "USA",
        phone: pf.phone || "", email: props.email || pf.email || "",
        recipientType: props.isStaff ? (props.recipientRole || "") : "",
        shipVia: "USPS", shipMethod: "", shipAccountNumber: "",
        // client review 2026-09-25/10-01: optional FF account number
        // (numeric, 6 digits — matching the admin allow-list waives the
        // daily limits, validated server-side) + notes (175 chars). The
        // old Domain field is gone: the company domain is derived from
        // the email address server-side, not typed (not honor-system).
        fairfieldAccountNumber: (pf.fairfieldAccountNumber || "").replace(/[^0-9]/g, "").slice(0, 6),
        requesterNotes: "",
        subscribe: true   // opt-out, not opt-in (client review 2026-09-23)
      },
      submitting: false,
      error: "",
      order: null
    };
  }

  setField = (name, value) => {
    this.setState(function (prev) {
      var form = { ...prev.form };
      form[name] = value;
      return { form: form };
    });
  };

  onInput = (e) => {
    this.setField(e.target.name, e.target.type === "checkbox" ? e.target.checked : e.target.value);
  };

  // 175-character cap with counter (client screenshot 2026-10-01)
  onNotesInput = (e) => {
    this.setField("requesterNotes", (e.target.value || "").slice(0, 175));
  };

  // Numeric only, 6-character limit (client screenshot 2026-10-01)
  onAccountNumberInput = (e) => {
    this.setField("fairfieldAccountNumber", (e.target.value || "").replace(/[^0-9]/g, "").slice(0, 6));
  };

  validate = () => {
    var f = this.state.form;
    var required = ["firstName", "lastName", "company", "address", "city", "postalCode", "country", "phone", "email", "shipVia"];
    if (!this.props.isStaff) required.push("recipientType");
    for (var i = 0; i < required.length; i++) {
      if (!(f[required[i]] || "").toString().trim()) {
        return "Please complete all required fields.";
      }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      return "Please enter a valid email address.";
    }
    if (f.shipVia !== "USPS" && !(f.shipAccountNumber || "").trim()) {
      return "An account number must be provided for expedited shipping services.";
    }
    return "";
  };

  onSubmit = (e) => {
    e.preventDefault();
    var self = this;
    var error = this.validate();
    if (error) {
      this.setState({ error: error });
      return;
    }
    this.setState({ submitting: true, error: "" });
    fetch("/api/FabricCart/Submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(this.state.form)
    })
      .then(function (r) {
        // Parse defensively: a 500 can return an HTML error page or a bare
        // ProblemDetails body — surface whatever cause we can find instead
        // of a blind generic (client screenshot 2026-10-01).
        return r.text().then(function (text) {
          var data = null;
          try { data = JSON.parse(text); } catch (ex) { /* non-JSON body */ }
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (res) {
        if (res.ok) {
          self.setState({ order: res.data, submitting: false });
          try {
            document.dispatchEvent(new CustomEvent("update-fabric-cart-counter", { detail: 0 }));
            document.dispatchEvent(new CustomEvent("fabric-order-submitted", {
              detail: {
                email: self.state.form.email,
                orderId: res.data.friendlyId,
                refId: res.data.refId,
                itemCount: res.data.count
              }
            }));
          } catch (ex) { /* no-op */ }
          window.scrollTo(0, 0);
        } else {
          var cause = res.data && (res.data.message || res.data.title);
          self.setState({
            error: cause ? cause : "Your order could not be submitted (server error " + res.status + "). Please try again, or contact us if it keeps happening.",
            submitting: false
          });
        }
      })
      .catch(function () {
        self.setState({ error: "Your order could not be submitted — we couldn't reach the server. Please check your connection and try again.", submitting: false });
      });
  };

  renderRegionOptions = () => {
    var opts = [<option key="_" value="">-- Select --</option>];
    opts.push(<option key="us" disabled>— United States —</option>);
    US_STATES.forEach(function (s) { opts.push(<option key={"us" + s} value={s}>{s}</option>); });
    opts.push(<option key="ca" disabled>— Canada —</option>);
    CA_PROVINCES.forEach(function (p) { opts.push(<option key={"ca" + p} value={p}>{p}</option>); });
    return opts;
  };

  render() {
    var f = this.state.form;
    var items = this.props.data || [];

    if (this.state.order) {
      var order = this.state.order;
      return (
        <div className="fabric-checkout-success">
          <h3>Your Fabric Sample Cart has been successfully submitted!</h3>
          <p><span className="medium">Order No:</span> {order.friendlyId}</p>
          <p><a className="ui-button secondary min-width ink" href={"/Sample-Cart/Order/" + order.refId}>VIEW YOUR ORDER</a></p>
        </div>
      );
    }

    var showCourierFields = f.shipVia !== "USPS";

    return (
      <div className="fabric-checkout">
        <form className="fabric-checkout-form" onSubmit={this.onSubmit} noValidate>
          <div className="fabric-checkout-grid">
            <div className="fc-field">
              <label>First Name <span className="fc-req">*</span></label>
              <input type="text" name="firstName" value={f.firstName} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Last Name <span className="fc-req">*</span></label>
              <input type="text" name="lastName" value={f.lastName} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Company <span className="fc-req">*</span></label>
              <input type="text" name="company" value={f.company} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Fairfield Account Number</label>
              <input type="text" name="fairfieldAccountNumber" value={f.fairfieldAccountNumber}
                     inputMode="numeric" maxLength={6} onChange={this.onAccountNumberInput} />
            </div>
            <div className="fc-field">
              <label>Email <span className="fc-req">*</span></label>
              <input type="email" name="email" value={f.email} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Address <span className="fc-req">*</span></label>
              <input type="text" name="address" value={f.address} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Address 2</label>
              <input type="text" name="address2" value={f.address2} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>City <span className="fc-req">*</span></label>
              <input type="text" name="city" value={f.city} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>State / Province</label>
              <select name="region" value={f.region} onChange={this.onInput}>
                {this.renderRegionOptions()}
              </select>
            </div>
            <div className="fc-field">
              <label>Postal Code <span className="fc-req">*</span></label>
              <input type="text" name="postalCode" value={f.postalCode} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Country <span className="fc-req">*</span></label>
              <select name="country" value={f.country} onChange={this.onInput}>
                {COUNTRIES.map(function (c) { return <option key={c} value={c}>{c}</option>; })}
              </select>
            </div>
            <div className="fc-field">
              <label>Phone <span className="fc-req">*</span></label>
              <input type="tel" name="phone" value={f.phone} onChange={this.onInput} />
            </div>
            <div className="fc-field">
              <label>Recipient Type <span className="fc-req">*</span></label>
              {this.props.isStaff ? (
                <input type="text" name="recipientType" value={f.recipientType} readOnly />
              ) : (
                <select name="recipientType" value={f.recipientType} onChange={this.onInput}>
                  <option value="">-- Select --</option>
                  {/* client review 2026-09-22: manual selection for non-staff */}
                  <option value="Consumer">Consumer</option>
                  <option value="Residential (Designer)">Residential (Designer)</option>
                  <option value="Residential (Retail)">Residential (Retail)</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Senior Living">Senior Living</option>
                </select>
              )}
            </div>
            <div className="fc-field">
              <label>Ship Via <span className="fc-req">*</span></label>
              <select name="shipVia" value={f.shipVia} onChange={this.onInput}>
                {SHIP_VIA.map(function (s) { return <option key={s.value} value={s.value}>{s.label}</option>; })}
              </select>
              {/* client 2026-10-16 (ticket 86e3re32m) */}
              <div className="fc-field-hint">US Mail can take up to 2 weeks to receive and will not have tracking.</div>
            </div>
            {showCourierFields && (
              <div className="fc-field">
                <label>Ship Method</label>
                <input type="text" name="shipMethod" value={f.shipMethod} onChange={this.onInput} placeholder="e.g. Ground, 2-Day" />
              </div>
            )}
            {showCourierFields && (
              <div className="fc-field">
                <label>Shipping Account Number <span className="fc-req">*</span></label>
                <input type="text" name="shipAccountNumber" value={f.shipAccountNumber} onChange={this.onInput} />
                <div className="fc-field-hint">Required for all FedEx/UPS requests.</div>
              </div>
            )}
          </div>

          <div className="fc-field fc-field-notes">
            <label>Notes</label>
            <textarea name="requesterNotes" value={f.requesterNotes} onChange={this.onNotesInput}
                      rows={3} maxLength={175} placeholder="Anything we should know about your sample request..." />
            <div className="fc-field-hint fc-notes-counter">{(f.requesterNotes || "").length}/175</div>
          </div>

          <label className="fc-subscribe">
            <input type="checkbox" name="subscribe" checked={f.subscribe} onChange={this.onInput} />
            <span>Allow Fairfield to send email announcements and promotions.</span>
          </label>

          {this.state.error && <div className="fc-error">{this.state.error}</div>}

          <div className="fc-actions">
            <button type="submit" className="ui-button-configure ink fc-submit" disabled={this.state.submitting}>
              {this.state.submitting ? "SUBMITTING..." : "SUBMIT ORDER"}
            </button>
            <a href="/Sample-Cart" className="fc-back-link">Back to cart</a>
          </div>
        </form>

        <h4 className="fabric-checkout-items-title">Your Samples</h4>
        <FabricCartItems
          items={items}
          imageRootUrl={this.props.imageRootUrl}
          disableEditing={true}
        />
      </div>
    );
  }
}
